import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import type { SiteRagReport } from '../../../types/siteRag';
import { useStore } from '../../../store';
import { authedFetch } from '../../../lib/authedFetch';
import { loadFrozenSummaryReport } from '../../../lib/screeningSummary/loadFrozenSummaryReport';
import { screeningSummaryEligibility } from '../../../lib/screeningSummary/eligibility';
import { buildScreeningSummaryModel } from '../../../lib/screeningSummary/buildScreeningSummaryModel';
import { buildPowerCoolingAnswers } from '../../../lib/findings/powerCoolingAnswers';
import { reportIntentIsCurrent } from '../../../lib/pathToGo/reportPlan';
import { powerCoolingInputsAtGeneration } from '../../../lib/screeningSummary/powerCoolingInputsAtGeneration';
import { siteOverviewPath } from '../../../lib/siteNavigation';
import { ScreeningSummaryDocument } from './ScreeningSummaryDocument';
import { ReportSummaryView } from './ReportSummaryView';
import { SummaryToolbar } from './SummaryToolbar';
import { downloadScreeningSummary, prebuildScreeningSummary, resolveScreeningSummaryPageSize } from './summaryArtifactActions';

function formatScreeningModeLabel(mode: string | undefined): string {
  if (mode === 'evaluate-proposal') return 'Evaluate my proposal (evaluate-proposal)';
  if (mode === 'recommend') return 'Recommend an approach (recommend)';
  return mode ?? 'unspecified';
}

export function ScreeningSummaryView({ report }: { report: SiteRagReport }) {
  return (report.reportType ?? 'feasibility') === 'feasibility' ? <FrozenScreeningSummaryView report={report} /> : <ReportSummaryView report={report} />;
}

function FrozenScreeningSummaryView({ report }: { report: SiteRagReport }) {
  const [params, setParams] = useSearchParams();
  const [frozenPageSize, setFrozenPageSize] = useState<'A4' | 'Letter'>('A4');
  const pageSize = params.get('size') === 'letter' ? 'Letter' : params.get('size') === 'a4' ? 'A4' : frozenPageSize;
  const hydrate = useStore(s => s.hydrateReportBody);
  const companyId = useStore(s => s.companyId);
  const liveSite = useStore(s => (s.siteRag?.sites ?? (s as unknown as { sites?: Array<{ id: string; projectIntent?: SiteRagReport['projectIntentAtGen'] }> }).sites ?? []).find(site => site.id === report.siteId));
  const [body, setBody] = useState<SiteRagReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const summaryIdentity = [
    companyId ?? '',
    report.siteId ?? '',
    report.id,
    report.reportRevision?.revisionId ?? '',
    report.reportRevision?.documentHash ?? '',
  ].join('::');
  const summaryIdentityRef = React.useRef(summaryIdentity);
  const downloadRequestSeqRef = React.useRef(0);
  const lifecycleEpochRef = React.useRef(0);
  const isMountedRef = React.useRef(true);
  if (summaryIdentityRef.current !== summaryIdentity) {
    summaryIdentityRef.current = summaryIdentity;
    lifecycleEpochRef.current += 1;
    downloadRequestSeqRef.current += 1;
  }
  const isDownloadRequestCurrent = (
    requestIdentity: string,
    requestSeq: number,
    requestEpoch: number,
  ): boolean =>
    isMountedRef.current
    && lifecycleEpochRef.current === requestEpoch
    && summaryIdentityRef.current === requestIdentity
    && downloadRequestSeqRef.current === requestSeq;
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      lifecycleEpochRef.current += 1;
      downloadRequestSeqRef.current += 1;
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    const expectedCompanyId = companyId ?? null;
    setBody(null); setError(null); setDownloadError(null); setPending(false);
    void (async () => {
      let source = report;
      if (source._metadataOnly) {
        if (!await hydrate(report.id)) throw new Error('The complete report could not be loaded.');
        if (cancelled || (useStore.getState().companyId ?? null) !== expectedCompanyId) return;
        const hydrated = (useStore.getState().siteRag?.reports ?? []).find(r => r.id === report.id);
        if (!hydrated || hydrated.reportRevision?.revisionId !== report.reportRevision?.revisionId) throw new Error('The screening revision changed while it was loading.');
        source = hydrated;
      }
      source = await loadFrozenSummaryReport(source, async path => {
        const res = await authedFetch(`/api/storage/load?path=${encodeURIComponent(path)}`);
        if (!res.ok) throw new Error('The saved screening revision could not be loaded.');
        return res.json();
      });
      if (cancelled || (useStore.getState().companyId ?? null) !== expectedCompanyId) return;
      const eligible = screeningSummaryEligibility(source);
      if (!eligible.ok) throw new Error(eligible.reason);
      const paper = await resolveScreeningSummaryPageSize(source);
      if (!cancelled && (useStore.getState().companyId ?? null) === expectedCompanyId) {
        setFrozenPageSize(paper);
        setBody(source);
        const storeState = useStore.getState();
        const siteInStore = (storeState.siteRag?.sites ?? (storeState as unknown as { sites?: Array<{ id: string; projectIntent?: SiteRagReport['projectIntentAtGen'] }> }).sites ?? []).find(site => site.id === source.siteId);
        const isCurrentIntent = !siteInStore?.projectIntent || reportIntentIsCurrent(source, siteInStore.projectIntent);
        if (isCurrentIntent) {
          const targetPaper = params.get('size') === 'letter' ? 'Letter' : params.get('size') === 'a4' ? 'A4' : paper;
          if (expectedCompanyId) prebuildScreeningSummary(source, targetPaper, expectedCompanyId);
          else prebuildScreeningSummary(source, targetPaper);
        }
      }
    })().catch(e => {
      if (!cancelled && (useStore.getState().companyId ?? null) === expectedCompanyId) {
        setError((e as Error).message);
      }
    });
    return () => { cancelled = true; };
  }, [report, hydrate, companyId]);
  const intentDriftNotice = useMemo(() => {
    if (!body || !liveSite?.projectIntent) return null;
    if (reportIntentIsCurrent(body, liveSite.projectIntent)) return null;
    const frozenMode = body.projectIntentAtGen?.screeningMode;
    const liveMode = liveSite.projectIntent.screeningMode;
    if (frozenMode && liveMode && frozenMode !== liveMode) {
      return `Intent drift: This frozen revision was generated in "${formatScreeningModeLabel(frozenMode)}" mode, while live Project Intent is now "${formatScreeningModeLabel(liveMode)}". Rerun Screening Assessment to generate a new revision and PDF for the current Project Intent.`;
    }
    return 'Intent drift: Live Project Intent has changed since this frozen revision was generated. Rerun Screening Assessment to generate a new revision and PDF for the current Project Intent.';
  }, [body, liveSite?.projectIntent]);
  const model = useMemo(() => body ? buildScreeningSummaryModel(body, buildPowerCoolingAnswers(powerCoolingInputsAtGeneration(body))) : null, [body]);
  if (error) return <div className="p-8"><h1>Screening summary unavailable</h1><p>{error}</p><Link to={siteOverviewPath(report.siteId ?? '', 'reports')}>Back to Analyses</Link></div>;
  if (!body || !model) return <div className="p-8" role="status">Loading the saved screening summary…</div>;
  return <ScreeningSummaryDocument report={body} pageSize={pageSize}>
    <SummaryToolbar model={model} siteId={body.siteId ?? ''} pageSize={pageSize} pending={pending}
      intentDriftNotice={intentDriftNotice}
      downloadError={downloadError}
      onPageSize={size => {
        setParams({ size: size.toLowerCase() }, { replace: true });
        if (!intentDriftNotice) {
          if (companyId) prebuildScreeningSummary(body, size, companyId);
          else prebuildScreeningSummary(body, size);
        }
      }}
      onDownload={() => {
        const requestIdentity = summaryIdentity;
        const requestEpoch = lifecycleEpochRef.current;
        const requestSeq = ++downloadRequestSeqRef.current;
        setPending(true);
        setDownloadError(null);
        void downloadScreeningSummary(body, {
          pageSize,
          companyId: companyId ?? null,
          shouldSave: () => isDownloadRequestCurrent(requestIdentity, requestSeq, requestEpoch),
        })
          .catch(e => {
            if (isDownloadRequestCurrent(requestIdentity, requestSeq, requestEpoch)) {
              const msg = (e as Error).message;
              setDownloadError(msg);
              toast.error(msg);
            }
          })
          .finally(() => {
            if (isDownloadRequestCurrent(requestIdentity, requestSeq, requestEpoch)) {
              setPending(false);
            }
          });
      }} />
  </ScreeningSummaryDocument>;
}
